import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useRefreshShiftsRows } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { ADD_SHIFT_OPTIONS } from 'modules/shift_management/lib/addShiftOptions'
import { trackDeleteUnassignedShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import type { ShiftsRow, UnassignedShift } from 'modules/shift_management/lib/types'

import { useUnassignedShifts } from './useUnassignedShifts'

const trackSubmitEvent = (unassignedShift: UnassignedShift) => {
  const shiftType = unassignedShift.isDefault
    ? ADD_SHIFT_OPTIONS.defaultShift
    : ADD_SHIFT_OPTIONS.customShift
  const counter = Number(unassignedShift.counter)

  trackDeleteUnassignedShiftSubmit(unassignedShift, shiftType, counter, 'contextual_button')
}

const useDestroyUnassignedShift = (
  unassignedShift: UnassignedShift,
  contractVersions: ContractVersions
) => {
  const [, dispatchUnassignedShifts] = useUnassignedShifts()
  const [{ shiftsRows }] = useShiftsRows()
  const { refreshShiftsRows } = useRefreshShiftsRows(contractVersions)

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      trackSubmitEvent(unassignedShift)

      const result = await asyncAction(() => unassignedShift.toPayload().delete(), {
        successMessage: i18n.t('shift_management.actions.delete_unassigned_shifts_success'),
      })

      if (isSuccessful(result)) {
        dispatchUnassignedShifts({ type: 'DESTROY_UNASSIGNED_SHIFT', unassignedShift })
        const relatedShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
          return row.shifts.filterByUnassignedShift(unassignedShift.id)
        })
        await refreshShiftsRows(relatedShiftsRows, [])

        closePortal()
      }
    },
    {
      successFlash: false,
      errorFlash: false,
    }
  )

  return {
    onDestroy: performAction,
    submitting,
  }
}

export { useDestroyUnassignedShift }
